import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../../../components/sidebar";
import {
  MainContainer,
  BackgroundPattern,
  SideContainer,
  ContentWrapper,
  HeaderSection,
  Title,
  Subtitle,
  ButtonRow,
  ButtonContainerLeft,
  StyledButton,
} from "../../ListAreas/styles";
import {
  FormContainer,
  FormRow,
  Label,
  Input,
  CheckboxWrapper,
  Checkbox,
  FileInputWrapper,
  ModeTabs,
  ModeTab,
  BboxPreview,
  ErrorText,
} from "./styles";
import { firesService } from "../../../../services/firesService";
import { guardianAreaService } from "../../../../services/guardianAreaService";
import { appStatus } from "../../../../store/modules/app_status/actions";
import { GUARDIAN_PANEL } from "../../../../utils/appNavigation";

/**
 * Cadastro de área FIRMS (bbox ou KML).
 * Rota dedicada — não depende de app_status do shell Welcome.
 */
const CreateArea = () => {
  const { id_code } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const collapsed = useSelector((state) => state.sidebar);

  const [guardianAreaId, setGuardianAreaId] = useState(null);
  const [guardianAreas, setGuardianAreas] = useState([]);
  const [areaName, setAreaName] = useState("");
  const [mode, setMode] = useState("bbox"); // "bbox" | "kml"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    bbox_west: "",
    bbox_south: "",
    bbox_east: "",
    bbox_north: "",
    active: true,
  });
  const [kmlFile, setKmlFile] = useState(null);
  const [bboxPreview, setBboxPreview] = useState(null);

  useEffect(() => {
    dispatch(appStatus(GUARDIAN_PANEL));
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      if (id_code) {
        try {
          const res = await guardianAreaService.getGuardianAreaById(id_code);
          const a = res?.data || res;
          const uuid = a?.id || a?.id_code;
          setGuardianAreaId(uuid);
          setAreaName(a?.name || "");
        } catch {
          setGuardianAreaId(null);
        }
        return;
      }

      try {
        const data = await guardianAreaService.getGuardianAreas();
        const list = Array.isArray(data) ? data : [];
        setGuardianAreas(list);
        if (list.length > 0) {
          const firstId = list[0]?.id || list[0]?.id_code;
          setGuardianAreaId(firstId || null);
        }
      } catch {
        setGuardianAreas([]);
      }
    };

    fetchData();
  }, [id_code]);

  const handleKmlChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.toLowerCase().split(".").pop();
    if (ext !== "kml" && ext !== "kmz") {
      setError("Aceito apenas arquivos .kml ou .kmz");
      return;
    }
    setKmlFile(file);
    setError("");
    try {
      const result = await firesService.extractBboxFromKml(file);
      setBboxPreview(result);
    } catch {
      setBboxPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name?.trim()) {
      setError("Nome da área é obrigatório.");
      return;
    }

    if (mode === "bbox") {
      const west = parseFloat(formData.bbox_west);
      const south = parseFloat(formData.bbox_south);
      const east = parseFloat(formData.bbox_east);
      const north = parseFloat(formData.bbox_north);
      if (isNaN(west) || isNaN(south) || isNaN(east) || isNaN(north)) {
        setError("Preencha todos os campos da bbox com números válidos.");
        return;
      }
      if (west >= east || south >= north) {
        setError("Bbox inválida: west < east e south < north.");
        return;
      }
    } else {
      if (!kmlFile) {
        setError("Selecione um arquivo KML ou KMZ.");
        return;
      }
    }

    if (!guardianAreaId) {
      setError("Área Guardian não encontrada.");
      return;
    }

    setLoading(true);
    try {
      if (mode === "bbox") {
        const payload = {
          name: formData.name.trim(),
          bbox_west: parseFloat(formData.bbox_west),
          bbox_south: parseFloat(formData.bbox_south),
          bbox_east: parseFloat(formData.bbox_east),
          bbox_north: parseFloat(formData.bbox_north),
          active: formData.active,
          guardian_area: guardianAreaId,
        };
        await firesService.createArea(payload);
      } else {
        const formDataToSend = new FormData();
        formDataToSend.append("name", formData.name.trim());
        formDataToSend.append("kml_file", kmlFile);
        formDataToSend.append("active", String(formData.active));
        formDataToSend.append("guardian_area", guardianAreaId);
        await firesService.createAreaWithKml(formDataToSend);
      }
      if (id_code) navigate(`/area_details/${id_code}`, { state: { activeTab: "firms" } });
      else navigate("/areas");
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "Erro ao criar área.";
      setError(typeof msg === "object" ? JSON.stringify(msg) : msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainContainer collapsed={collapsed}>
      <BackgroundPattern />
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <ContentWrapper>
          <ButtonRow>
            <ButtonContainerLeft>
              <StyledButton
                onClick={() =>
                  id_code
                    ? navigate(`/area_details/${id_code}`, {
                        state: { activeTab: "firms" },
                      })
                    : navigate("/areas")
                }
              >
                Voltar
              </StyledButton>
            </ButtonContainerLeft>
          </ButtonRow>

          <HeaderSection>
            <Title>🔥 Nova Área FIRMS</Title>
            <Subtitle>
              {areaName
                ? `Área: ${areaName}`
                : "Cadastre uma área de monitoramento de incêndios"}
            </Subtitle>
          </HeaderSection>

          <FormContainer onSubmit={handleSubmit}>
            {!id_code && (
              <FormRow>
                <Label>Área Guardian *</Label>
                <Input
                  as="select"
                  value={guardianAreaId || ""}
                  onChange={(e) => setGuardianAreaId(e.target.value)}
                  required
                >
                  {guardianAreas.length === 0 ? (
                    <option value="">Nenhuma área disponível</option>
                  ) : (
                    guardianAreas.map((ga) => (
                      <option
                        key={ga.id || ga.id_code}
                        value={ga.id || ga.id_code}
                      >
                        {ga.name || ga.id_code}
                      </option>
                    ))
                  )}
                </Input>
              </FormRow>
            )}

            <ModeTabs>
              <ModeTab
                $active={mode === "bbox"}
                onClick={() => {
                  setMode("bbox");
                  setError("");
                }}
              >
                Bbox manual
              </ModeTab>
              <ModeTab
                $active={mode === "kml"}
                onClick={() => {
                  setMode("kml");
                  setError("");
                }}
              >
                Arquivo KML/KMZ
              </ModeTab>
            </ModeTabs>

            <FormRow>
              <Label>Nome da área *</Label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Ex: Área Amazônia Norte"
                required
              />
            </FormRow>

            {mode === "bbox" ? (
              <>
                <FormRow>
                  <Label>Bbox: Oeste (longitude)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.bbox_west}
                    onChange={(e) =>
                      setFormData({ ...formData, bbox_west: e.target.value })
                    }
                    placeholder="-60"
                  />
                </FormRow>
                <FormRow>
                  <Label>Bbox: Sul (latitude)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.bbox_south}
                    onChange={(e) =>
                      setFormData({ ...formData, bbox_south: e.target.value })
                    }
                    placeholder="-10"
                  />
                </FormRow>
                <FormRow>
                  <Label>Bbox: Leste (longitude)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.bbox_east}
                    onChange={(e) =>
                      setFormData({ ...formData, bbox_east: e.target.value })
                    }
                    placeholder="-45"
                  />
                </FormRow>
                <FormRow>
                  <Label>Bbox: Norte (latitude)</Label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.bbox_north}
                    onChange={(e) =>
                      setFormData({ ...formData, bbox_north: e.target.value })
                    }
                    placeholder="2"
                  />
                </FormRow>
              </>
            ) : (
              <>
                <FormRow>
                  <Label>Arquivo KML ou KMZ</Label>
                  <FileInputWrapper>
                    <input
                      type="file"
                      accept=".kml,.kmz"
                      onChange={handleKmlChange}
                    />
                  </FileInputWrapper>
                </FormRow>
                {bboxPreview && (
                  <BboxPreview>Bbox extraída: {bboxPreview.bbox_str}</BboxPreview>
                )}
              </>
            )}

            <CheckboxWrapper>
              <Checkbox
                type="checkbox"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
              />
              <Label as="span" style={{ margin: 0, marginLeft: "0.5rem" }}>
                Área ativa para scan
              </Label>
            </CheckboxWrapper>

            {error && <ErrorText>{error}</ErrorText>}

            <StyledButton type="submit" disabled={loading}>
              {loading ? "Salvando..." : "Criar área"}
            </StyledButton>
          </FormContainer>
        </ContentWrapper>
      </SideContainer>
    </MainContainer>
  );
};

export default CreateArea;
